// Fallback Web Speech API Service (browser-based)
class WebSpeechService {
  constructor() {
    this.recognition = null;
    this.isListening = false;
    this.currentTranscript = '';
    this.currentResolve = null;
    this.currentReject = null;
    this.hasResolved = false;
  }

  // Check if browser supports Web Speech API
  isSupported() {
    return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  }

  // Start speech recognition
  async startRecording() {
    if (!this.isSupported()) {
      throw new Error('Web Speech API not supported in this browser');
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    this.recognition = new SpeechRecognition();

    // Configure recognition for better accuracy and longer speech
    this.recognition.continuous = true; // Allow continuous listening
    this.recognition.interimResults = true; // Allow interim results
    this.recognition.lang = 'en-US';
    this.recognition.maxAlternatives = 5; // Get more alternatives for better accuracy
    
    // Additional settings for better performance
    if ('webkitSpeechGrammarList' in window) {
      const grammar = '#JSGF V1.0; grammar transaction; public <transaction> = bought | purchased | buy | hairdryer | shampoo | scissors | towels | euro | euros | cash | card | and | for;';
      const speechRecognitionList = new window.webkitSpeechGrammarList();
      speechRecognitionList.addFromString(grammar, 1);
      this.recognition.grammars = speechRecognitionList;
    }
    
    console.log('Web Speech API: Configuration set:', {
      continuous: this.recognition.continuous,
      interimResults: this.recognition.interimResults,
      lang: this.recognition.lang,
      maxAlternatives: this.recognition.maxAlternatives
    });

    return new Promise((resolve, reject) => {
      // Store resolve/reject for manual stopping
      this.currentResolve = resolve;
      this.currentReject = reject;
      this.hasResolved = false; // Reset resolved flag
      this.currentTranscript = ''; // Reset transcript

      this.recognition.onstart = () => {
        this.isListening = true;
        console.log('Web Speech API: Started listening');
      };

      let fullTranscript = '';
      
      this.recognition.onresult = (event) => {
        console.log('Web Speech API: Raw results:', event.results);
        
        // Build complete transcript from all results
        let currentTranscript = '';
        
        for (let i = 0; i < event.results.length; i++) {
          const result = event.results[i];
          
          // Choose the best alternative (highest confidence)
          let bestAlternative = result[0];
          for (let j = 1; j < result.length; j++) {
            if (result[j].confidence > bestAlternative.confidence) {
              bestAlternative = result[j];
            }
          }
          
          console.log(`Web Speech API: Result ${i}:`, {
            transcript: bestAlternative.transcript,
            confidence: bestAlternative.confidence,
            isFinal: result.isFinal,
            alternatives: result.length
          });
          
          currentTranscript += `${currentTranscript ? ' ' : ''}${bestAlternative.transcript}`;
        }
        
        fullTranscript = currentTranscript;
        console.log('Web Speech API: Current full transcript:', fullTranscript);
        
        // Store the transcript for manual stopping
        this.currentTranscript = fullTranscript;
      };

      this.recognition.onerror = (event) => {
        console.error('Web Speech API error:', event.error);
        if (!this.hasResolved) {
          this.hasResolved = true;
          this.isListening = false;
          
          let errorMessage = 'Speech recognition failed';
          switch(event.error) {
            case 'no-speech':
              errorMessage = 'No speech detected. Please try again.';
              break;
            case 'audio-capture':
              errorMessage = 'Microphone not accessible. Please check permissions.';
              break;
            case 'not-allowed':
              errorMessage = 'Microphone permission denied. Please allow microphone access.';
              break;
            case 'network':
              errorMessage = 'Network error. Please check your connection.';
              break;
            default:
              errorMessage = `Speech recognition error: ${event.error}`;
          }
          
          this.currentReject(new Error(errorMessage));
          this.currentResolve = null;
          this.currentReject = null;
        }
      };

      this.recognition.onend = () => {
        this.isListening = false;
        console.log('Web Speech API: Recognition ended, hasResolved:', this.hasResolved);
        
        // If we have a transcript but haven't resolved yet, use it
        if (fullTranscript.trim() && !this.hasResolved) {
          console.log('Web Speech API: Using transcript from onend:', fullTranscript);
          this.hasResolved = true;
          this.currentResolve(fullTranscript.trim());
          this.currentResolve = null;
          this.currentReject = null;
        } else if (!this.hasResolved) {
          this.hasResolved = true;
          this.currentReject(new Error('No speech detected. Please try speaking again.'));
          this.currentResolve = null;
          this.currentReject = null;
        }
      };

      this.recognition.start();
    });
  }

  // Stop recording manually
  stopRecording() {
    if (this.recognition && this.isListening && !this.hasResolved) {
      console.log('Web Speech API: Manual stop requested');
      
      this.isListening = false;
      this.hasResolved = true; // Mark as resolved to prevent onend from interfering
      
      // Stop the recognition
      this.recognition.stop();
      
      // Resolve with current transcript if available
      if (this.currentTranscript && this.currentTranscript.trim() && this.currentResolve) {
        console.log('Web Speech API: Resolving with current transcript:', this.currentTranscript);
        this.currentResolve(this.currentTranscript.trim());
      } else if (this.currentReject) {
        console.log('Web Speech API: No transcript available, rejecting');
        this.currentReject(new Error('No speech detected. Please try speaking again.'));
      }
      
      // Clean up
      this.currentResolve = null;
      this.currentReject = null;
    } else {
      console.log('Web Speech API: Stop requested but not listening or already resolved');
    }
  }

  // Main method to record and transcribe
  async recordAndTranscribe() {
    try {
      const transcript = await this.startRecording();
      return transcript;
    } catch (error) {
      throw error;
    }
  }
}

export default new WebSpeechService();
